import { FilterLinesIcon } from '@langchain/untitled-ui-icons';

import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { Button, Text } from '@/design-system';
import {
  InstanceFlags,
  useInstanceFlagOrDefault,
} from '@/hooks/useInstanceFlag';
import { useGetFilterView } from '@/hooks/useSwr';
import { FilterViewType, RunStatsSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { CopyIconButton } from '../CopyButton/CopyButton';
import { InfoTooltip } from '../InfoTooltip/InfoTooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { astToString } from '../RunsTable/filter/ast';
import { SaveFilterPopover } from './FilterBar';
import { getActiveFieldsCount, useFilterAst } from './FilterBar.utils';
import { AddFilterButton } from './components/AddFilterButton';
import { ClearFilterButton } from './components/DeleteFilterButton';
import { FilterBarOperandsV2 } from './components/FilterBarOperands';
import { getConversationFields } from './fields/conversations';

const pluralRules = new Intl.PluralRules('en-US');

function FilterDialogContent(props: {
  stats: RunStatsSchema | undefined;
  value: string | undefined;
  onChange: (value: string | undefined) => void;

  setFeedbackUrls?: (urls: Record<string, string>) => void;

  onViewChange?: (newView: string, filter?: string) => void;
}) {
  const searchEnabled = useInstanceFlagOrDefault(
    InstanceFlags.search_enabled
  ) as boolean;
  const conversationFields = getConversationFields(searchEnabled);
  const [query, setQuery] = useFilterAst(props.value, conversationFields);

  const [feedbackUrlsByOperand, setFeedbackUrlsByOperand] = useState<
    Record<string, Record<string, string>>
  >({});

  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const filterViewId = searchParams.get('custom_filter_view_id');
  const { data: filterView, isLoading: filterViewLoading } = useGetFilterView(
    sessionId,
    filterViewId ?? undefined
  );

  return (
    <div className="group flex flex-col items-stretch border-tertiary">
      <div className="flex flex-col p-3 pb-1">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              {filterView?.display_name ?? 'Filters'}
            </span>

            <InfoTooltip
              description="Threads are shown if any run within them matches the selected filters."
              size="sm"
              icon="info"
            />
          </div>

          {props.value && (
            <div className="flex items-start gap-1 opacity-0 transition-all focus-within:opacity-100 group-hover:opacity-100 [&:has([data-state=open])]:opacity-100">
              {props.value && props.onViewChange && (
                <div className="flex justify-end gap-2">
                  <SaveFilterPopover
                    className="text-xxs"
                    filter={props.value}
                    filterView={filterView}
                    onViewChange={props.onViewChange}
                    isLoading={filterViewLoading}
                    saveFilterType={FilterViewType.THREADS}
                  />
                </div>
              )}

              <CopyIconButton
                className="p-1 text-quaternary"
                copy={props.value ?? ''}
                copyText={
                  <span>
                    Copy the filter(s) to use when filtering via{' '}
                    <a
                      href="https://api.smith.langchain.com/redoc#tag/run/operation/query_runs_api_v1_runs_query_post"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      API
                    </a>{' '}
                    or{' '}
                    <a
                      href="https://docs.smith.langchain.com/reference/python/client/langsmith.client.Client#langsmith.client.Client.list_runs"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      SDK
                    </a>
                  </span>
                }
              />
              <ClearFilterButton
                hasFilters={query.length > 0}
                onDeleteFilterValues={() => {
                  setQuery([]);
                  props.onChange(undefined);
                  props.onViewChange?.('', '');
                }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2 px-3 pb-3">
        <FilterBarOperandsV2
          fields={conversationFields}
          operandsWithFields={
            query.length === 0
              ? [{ field: undefined, comparisons: [{}] }]
              : query
          }
          handleChange={(value) => {
            const newQuery = setQuery(value);
            props.onChange(astToString(newQuery));
          }}
          stats={props.stats}
          feedbackUrlsByOperand={feedbackUrlsByOperand}
          setFeedbackUrlsByOperand={setFeedbackUrlsByOperand}
        />

        {query.length > 0 && (
          <AddFilterButton
            variant="v2"
            hideFilterTitle={false}
            hasFilters={query.length > 0}
            onClick={() => {
              props.onChange(
                astToString(setQuery([...query, { comparisons: [{}] }]))
              );
            }}
          />
        )}
      </div>
    </div>
  );
}

export function ConversationFilterBar(props: {
  stats: RunStatsSchema | undefined;

  value: string | undefined;
  onChange: (value: string | undefined) => void;

  feedbackUrls?: Record<string, string>;
  setFeedbackUrls?: (urls: Record<string, string>) => void;

  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;

  onViewChange?: (newView: string, filter?: string) => void;
}) {
  const { open, setOpen } = props;
  const searchEnabled = useInstanceFlagOrDefault(
    InstanceFlags.search_enabled
  ) as boolean;

  const conversationFields = getConversationFields(searchEnabled);

  const nActive = useMemo(
    () => getActiveFieldsCount({ filter: props.value }, conversationFields),
    [props.value]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outlined"
          color={nActive > 0 ? 'primary' : 'secondary'}
          size="sm"
          leftDecorator={FilterLinesIcon}
        >
          {nActive === 0 ? (
            <Text variant="sm">Filters</Text>
          ) : (
            <Text variant="sm">
              {
                {
                  one: `1 filter`,
                  two: `${nActive} filters`,
                  few: `${nActive} filters`,
                  other: `${nActive} filters`,
                }[pluralRules.select(nActive)]
              }
            </Text>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className={cn('z-10 w-auto min-w-[300px] overflow-hidden p-0')}
        avoidCollisions={false}
        side={'bottom'}
      >
        <FilterDialogContent
          stats={props.stats}
          value={props.value}
          onChange={props.onChange}
          setFeedbackUrls={props.setFeedbackUrls}
        />
      </PopoverContent>
    </Popover>
  );
}
